import React from "react";
import { MS_TO_KNOTS, PullTestResult } from "@/services/PushStationService";
import {
  JsonPullMapping,
  degToCompass,
  describeErrorCode,
  describeMissingField,
  formatInZone,
  normalizeMapping,
  toEpochMs,
  validateMapping,
} from "@/utils/jsonMapping";

/**
 * ONE status line for the JSON pull test, always describing the CURRENT url + mapping
 * (Ali, 2026-10-29: "it's very unclear when to revalidate"). The wizard re-tests
 * automatically, so the operator never decides when to test — this line only says where
 * things stand:
 *
 *   testing    — a test for this exact url + mapping is scheduled or in flight
 *   passed     — the server returned ok for exactly this url + mapping (with the sample)
 *   incomplete — nothing to test yet; says what to pick next
 *   failed     — the server test for exactly this url + mapping failed
 *
 * A result produced for any OTHER key is ignored here, so an edit after a pass can never
 * leave a stale green line behind.
 */

export type JsonTestStatusKind = "testing" | "passed" | "incomplete" | "failed";

export interface JsonTestStatusInput {
  url: string;
  mapping: JsonPullMapping | null;
  /** A test for the current key is in flight, or the debounced auto-test is pending. */
  busy: boolean;
  /** The latest result, but ONLY if it was produced for the current url + mapping. */
  currentResult: PullTestResult | null;
}

export interface JsonTestStatusValue {
  kind: JsonTestStatusKind;
  text: string;
}

export function computeJsonTestStatus(i: JsonTestStatusInput): JsonTestStatusValue {
  if (!i.url.trim().startsWith("https://")) {
    return { kind: "incomplete", text: "Not ready — enter an https poll URL" };
  }
  if (!i.mapping) {
    return { kind: "incomplete", text: "Mapping incomplete — fetch the URL or pick a preset" };
  }
  const errs = validateMapping(normalizeMapping(i.mapping));
  if (errs.length) return { kind: "incomplete", text: `Mapping incomplete — ${describeMissingField(errs)}` };
  if (i.busy || !i.currentResult) return { kind: "testing", text: "Testing…" };
  if (i.currentResult.ok) return { kind: "passed", text: "✓ Test passed for this mapping" };
  return {
    kind: "failed",
    text: `✗ Test failed: ${describeErrorCode(i.currentResult.error) || "no usable data"}`,
  };
}

const kn = (ms: number | null | undefined) =>
  ms == null || !Number.isFinite(ms) ? "—" : `${(ms * MS_TO_KNOTS).toFixed(1)} kn`;

const ageText = (ageMs: number): string => {
  const a = Math.abs(ageMs);
  const mins = Math.round(a / 60000);
  const txt = mins < 90 ? `${mins} min` : `${(a / 3600000).toFixed(1)} h`;
  return ageMs < 0 ? `${txt} in the future` : `${txt} ago`;
};

const TONE: Record<JsonTestStatusKind, string> = {
  testing: "border-gray-600 bg-gray-800 text-gray-200",
  passed: "border-green-700 bg-green-900/40 text-green-200",
  incomplete: "border-gray-600 bg-gray-800 text-gray-300",
  failed: "border-red-700 bg-red-900/40 text-red-200",
};

const JsonTestStatus: React.FC<{
  status: JsonTestStatusValue;
  result: PullTestResult | null;
  /** Zone to show the observation in, next to UTC (the mapping's local tz, else default). */
  displayTz: string;
  /** Manual retry — enabled whenever the mapping is complete and nothing is running. */
  onTestAgain?: () => void;
  canTestAgain: boolean;
}> = ({ status, result, displayTz, onTestAgain, canTestAgain }) => {
  const sample = status.kind === "passed" ? result?.sample ?? null : null;
  const observed = sample ? toEpochMs(sample.observedAt) : null;
  return (
    <div
      data-testid="json-test-status"
      data-status={status.kind}
      role="status"
      aria-live="polite"
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded border px-3 py-2 text-sm ${TONE[status.kind]}`}
    >
      {status.kind === "testing" && (
        <span
          aria-hidden="true"
          className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-400 border-t-transparent"
        />
      )}
      <span className="font-medium">{status.text}</span>
      {sample && (
        <span className="text-xs text-green-100/90">
          wind {kn(sample.windMs)} · gust {kn(sample.gustMs)} · dir{" "}
          {sample.dirDeg == null ? "—" : `${Math.round(sample.dirDeg)}° ${degToCompass(sample.dirDeg)}`}
          {observed != null && (
            <>
              {" "}
              · observed {formatInZone(observed, "UTC")} UTC / {formatInZone(observed, displayTz)}{" "}
              {displayTz} · {ageText(Date.now() - observed)}
            </>
          )}
        </span>
      )}
      {status.kind === "failed" && result?.error && (
        <span className="font-mono text-xs opacity-70">({result.error})</span>
      )}
      {onTestAgain && (status.kind === "passed" || status.kind === "failed") && (
        <button
          type="button"
          onClick={onTestAgain}
          disabled={!canTestAgain}
          className="ml-auto text-xs text-cyan-300 underline decoration-dotted hover:text-cyan-200 disabled:opacity-50"
        >
          Test again
        </button>
      )}
    </div>
  );
};

export default JsonTestStatus;
